import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import SwiperCore, { Navigation } from 'swiper/core';
import styled, { css, keyframes } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useRouter } from 'next/router';

import { FUTURE, PRESENT } from 'timeline/enums/constants';
import { GRIDS_WIDTH, TIMELINE_NAVIGATION_DESKTOP_HEIGHT } from 'search/styles/SearchStyles';
import { ITimelineYears } from 'timeline/types/timelineTypes';
import { TimelineNavigationButton } from 'timeline/components/TimelineNavigationButton';
import { variableFontRegular } from 'styles/theme';
import { timelineSwiperBreakpoints } from 'timeline/utils/timelineSwiperBreakpoints';
import { useBanner } from 'shared/hooks';
import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';

SwiperCore.use([Navigation]);

export const TimelineNavigation = ({
  timelineYears,
  showTimelineNavigationOnMobile = true,
}: {
  timelineYears: ITimelineYears;
  showTimelineNavigationOnMobile?: boolean;
}) => {
  const { query } = useRouter();
  const { timelineYear, brand } = query;
  const timePeriod = timelineYear ? timelineYear[0]?.toLowerCase() : PRESENT;
  const [swiper, setSwiper] = useState(null);
  const { isBannerEnabled, title, subtitle } = useBanner();
  const isBannerVisible = isBannerEnabled && (title || subtitle);
  const brandsQuery = Array.isArray(brand) ? brand : [brand];

  useEffect(() => {
    if (swiper) {
      // slide to selected timePeriod
      const selectedTimePeriodIndex = timelineYears.indexOf(timePeriod);
      if (selectedTimePeriodIndex > -1) {
        swiper.slideTo(selectedTimePeriodIndex, 300);
      }
    }
  }, [swiper, timelineYears?.length]);

  return (
    <TimelineNavigation.Wrapper
      $isBannerVisible={isBannerVisible}
      $showTimelineNavigationOnMobile={showTimelineNavigationOnMobile}
      $moveTimelineNavigationUpOnMobile={!showTimelineNavigationOnMobile}
    >
      <Link
        href={{
          pathname: '/timeline/future',
          query: {
            ...(brand && { brand: brandsQuery }),
          },
        }}
        passHref
        scroll
        shallow
      >
        <TimelineNavigation.FutureTimePeriod
          $isSelected={timePeriod === FUTURE}
          data-qa="time_period_future"
        >
          Future
        </TimelineNavigation.FutureTimePeriod>
      </Link>
      <Link
        href={{
          pathname: '/timeline',
          query: {
            ...(brand && { brand: brandsQuery }),
          },
        }}
        passHref
        scroll
        shallow
      >
        <TimelineNavigation.PresentTimePeriod
          $isSelected={timePeriod === PRESENT}
          data-qa="time_period_present"
        >
          Present
        </TimelineNavigation.PresentTimePeriod>
      </Link>
      <TimelineNavigation.VerticalDivider />
      <TimelineNavigation.PastYears data-qa="timeline_past_years_wrapper">
        <TimelineNavigation.SwiperCustomWrapper>
          <TimelineNavigation.Animation $numOfYears={timelineYears.length}>
            <Swiper
              onSwiper={setSwiper}
              grabCursor
              slidesPerView="auto"
              touchEventsTarget="container"
              freeMode
              threshold={8}
              breakpoints={timelineSwiperBreakpoints}
              navigation={{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }}
            >
              <TimelineNavigationButton direction="left" />
              {timelineYears.map((year) => (
                <SwiperSlide key={year}>
                  <Link
                    href={{
                      pathname: `/timeline/${year}`,
                      query: {
                        ...(brand && { brand: brandsQuery }),
                      },
                    }}
                    passHref
                    scroll
                    shallow
                  >
                    <TimelineNavigation.TimePeriod
                      $isSelected={timePeriod === year}
                      data-qa={`time_period_${year}`}
                    >
                      {year}
                    </TimelineNavigation.TimePeriod>
                  </Link>
                </SwiperSlide>
              ))}
              <TimelineNavigationButton direction="right" />
            </Swiper>
          </TimelineNavigation.Animation>
        </TimelineNavigation.SwiperCustomWrapper>
      </TimelineNavigation.PastYears>
    </TimelineNavigation.Wrapper>
  );
};

TimelineNavigation.Wrapper = styled.nav<{
  $isBannerVisible: boolean;
  $showTimelineNavigationOnMobile: boolean;
  $moveTimelineNavigationUpOnMobile;
}>`
  display: flex;
  border-top: ${({ theme }) => theme.borders.black};
  background-color: ${({ theme }) => theme.borders.white};
  position: relative;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    position: fixed;
    background-color: ${({ theme }) => theme.colors.white};
    border-bottom: ${({ theme }) => theme.borders.black};
    top: ${({
      $isBannerVisible,
      $showTimelineNavigationOnMobile,
      $moveTimelineNavigationUpOnMobile,
    }) => {
      if ($isBannerVisible) {
        return $showTimelineNavigationOnMobile ? `calc(150px + ${MOBILE_BANNER_HEIGHT})` : `-160px`;
      }
      if ($moveTimelineNavigationUpOnMobile) {
        return $isBannerVisible ? `calc(68px + ${MOBILE_BANNER_HEIGHT})` : '68px';
      }
      return $showTimelineNavigationOnMobile ? '156px' : `-160px`;
    }};
    transition: all 0.3s ease-in;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: auto;
    width: ${GRIDS_WIDTH};
    border-top: none;
    height: ${TIMELINE_NAVIGATION_DESKTOP_HEIGHT};
  }
`;

const timePeriodStyles = css<{ $isSelected: boolean }>`
  ${variableFontRegular({ size: '11.5px', height: '16px' })};
  display: inline-block;
  cursor: pointer !important;
  padding: 15px 5px;
  margin: 0 2.5px;
  color: ${({ theme }) => theme.colors.black};
  border-bottom: ${({ $isSelected, theme }) =>
    $isSelected ? `2px solid ${theme.colors.black}` : '2px solid transparent'};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
    padding: 20px 8px;
    margin: 0;
    border-bottom: none;
    text-decoration: ${({ $isSelected }) => $isSelected && 'underline !important'};
    text-underline-offset: 2px;

    @media (hover: hover) {
      :hover {
        text-decoration: underline !important;
        text-underline-offset: 2px;
      }
    }
  }
`;

TimelineNavigation.TimePeriod = styled.a<{ $isSelected: boolean }>`
  ${timePeriodStyles};
`;

TimelineNavigation.FutureTimePeriod = styled(TimelineNavigation.TimePeriod)`
  margin: 0 10px 0 15px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 20px 8px 20px 0;
    margin: 0;
  }
`;

TimelineNavigation.PresentTimePeriod = styled(TimelineNavigation.TimePeriod)`
  margin: 0 15px 0 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-right: 16px;
  }
`;

TimelineNavigation.VerticalDivider = styled.div`
  border-right: ${({ theme }) => theme.borders.black};
  align-self: center;
  position: relative;
  height: 48px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 16px;
  }
`;

TimelineNavigation.PastYears = styled.div`
  width: 100%;
  overflow-x: hidden;
  position: relative;

  .swiper-slide:first-child {
    margin-left: 15px;
  }
`;

TimelineNavigation.SwiperCustomWrapper = styled.div`
  width: 100%;

  .swiper-container .swiper-wrapper {
    display: flex;
  }
`;

const slideIn = (navWidth) => keyframes`
    from {
      transform: ${`translateX(-${navWidth as number}px)`};
      opacity: 0.5;
    }
    to {
      transform: translateX(0%);
      opacity: 1;
    }
`;

TimelineNavigation.Animation = styled.div<{ $numOfYears: number }>`
  animation: ${({ $numOfYears }) => slideIn($numOfYears * 50)} 2s cubic-bezier(0.58, 0, 0, 1);

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    animation: ${({ $numOfYears }) => slideIn($numOfYears * 40)} 1s cubic-bezier(0.58, 0, 0, 1);
  }
`;
